// Created: 2026-09-08
// Updated: 2026-09-08
//
// One predicate: is this value a screen name that may be put into a URL PATH SEGMENT?
//
// It exists because a truthiness test is not that predicate, and the difference was live. The owner's
// craft-room read is gated `!!user && !!screen_name && !!idToken`, and the console still showed
// `GET /api/secure/craft-room/null` — 404, then retried — during profile creation, before any screen
// name existed. `encodeURIComponent(null)` is the four-character string `'null'`, and `!!'null'` is
// TRUE, so a stringified null walks straight through the gate that exists to stop exactly this, and the
// literal word `null` arrives at the backend as though it were somebody's handle.
//
// A stringified null enters the client at ONE untrusted seam: `localStorage.getItem('fpw_screen_name')`,
// the screen-name cursor's bootstrap in `context/AuthContext.tsx`. `localStorage` values are strings,
// so a `null` written by any earlier build of this app — or by hand — is indistinguishable from a real
// name to a truthiness check, and it PERSISTS across deploys in a long-lived browser profile. Current
// code guards its own writes; nothing guarded the read.

/** The literal strings JavaScript produces when a nullish value is stringified into a URL. */
const STRINGIFIED_NULLISH = new Set(['null', 'undefined', 'NaN'])

/**
 * True when `value` is a screen name safe to interpolate into a request path.
 *
 * Rejects: nullish, non-string, empty/whitespace-only, and the stringified-nullish words above.
 *
 * Deliberately NOT a format validator. The authoritative format lives on the server, and
 * `CreateProfile.tsx` applies `/^[a-zA-Z0-9_-]{3,24}$/` at the point of creation; duplicating that here
 * would be a second copy of a rule that can drift, and it would make this predicate reject legitimate
 * historical names that predate the current format. The only claim made here is the one the defect
 * needs: this is a real name and not the word for the absence of one.
 */
export function isUsableScreenName(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed === '') return false
  return !STRINGIFIED_NULLISH.has(trimmed)
}
